import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

export default function App() {
    const mountRef = useRef(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        const tooltip = tooltipRef.current;

        // ===== SETUP =====
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xffffff);

        const camera = new THREE.PerspectiveCamera(
            50,
            mount.clientWidth / mount.clientHeight,
            0.1,
            100
        );
        camera.position.set(6, 8, 10);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
        mount.appendChild(renderer.domElement);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;

        scene.add(new THREE.AmbientLight(0xffffff, 0.7));
        const dir = new THREE.DirectionalLight(0xffffff, 0.7);
        dir.position.set(10, 12, 7);
        scene.add(dir);

        // ===== CONTAINER =====
        const CW = 5, CH = 8, CD = 5;
        const outerGeo = new THREE.BoxGeometry(CW, CH, CD);
        const outerMat = new THREE.MeshBasicMaterial({ color: 0x111111, wireframe: true });
        const outerBox = new THREE.Mesh(outerGeo, outerMat);
        scene.add(outerBox);

        // ===== BOX DATA =====
        const count = 7;
        const rawBoxes = Array.from({ length: count }, () => ({
            w: THREE.MathUtils.randFloat(1, 3),
            h: THREE.MathUtils.randFloat(0.5, 2),
            d: THREE.MathUtils.randFloat(1, 3)
        }));

        const totalH = rawBoxes.reduce((s, b) => s + b.h, 0);
        const maxW = Math.max(...rawBoxes.map(b => b.w));
        const maxD = Math.max(...rawBoxes.map(b => b.d));

        const scale = Math.min(CW / maxW, CH / totalH, CD / maxD);
        const boxes = rawBoxes.map(b => ({
            w: b.w * scale,
            h: b.h * scale,
            d: b.d * scale
        }));

        // ===== CREATE BOXES =====
        const items = [];
        let yCursor = -CH / 2;

        boxes.forEach((b, i) => {
            const geo = new THREE.BoxGeometry(b.w, b.h, b.d);
            const mat = new THREE.MeshStandardMaterial({
                color: new THREE.Color(Math.random(), Math.random(), Math.random())
            });

            const mesh = new THREE.Mesh(geo, mat);
            mesh.position.y = yCursor + b.h / 2;
            mesh.position.x = THREE.MathUtils.randFloatSpread((CW - b.w) * 0.3);
            mesh.position.z = THREE.MathUtils.randFloatSpread((CD - b.d) * 0.3);

            mesh.userData = {
                index: i,
                width: b.w.toFixed(2),
                height: b.h.toFixed(2),
                depth: b.d.toFixed(2),
                volume: (b.w * b.h * b.d).toFixed(2)
            };

            yCursor += b.h;
            items.push(mesh);
            scene.add(mesh);
        });

        // ===== RAYCASTING =====
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();
        let hoverObject = null;

        const handleMouseMove = (e) => {
            const rect = renderer.domElement.getBoundingClientRect();

            mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(items);

            if (intersects.length > 0) {
                const obj = intersects[0].object;

                if (hoverObject !== obj) {
                    if (hoverObject) hoverObject.material.emissive.set(0x000000);
                    hoverObject = obj;
                    hoverObject.material.emissive.set(0x444444);
                }

                const d = obj.userData;

                tooltip.style.display = "block";
                tooltip.style.left = e.clientX + 15 + "px";
                tooltip.style.top = e.clientY + 15 + "px";

                tooltip.innerHTML = `
      <b>Box #${d.index}</b><br>
      Width: ${d.width}<br>
      Height: ${d.height}<br>
      Depth: ${d.depth}<br>
      Volume: ${d.volume}
    `;
            } else {
                if (hoverObject) hoverObject.material.emissive.set(0x000000);
                hoverObject = null;
                tooltip.style.display = "none";
            }

            console.log('asdas')
        };

        mount.addEventListener("mousemove", handleMouseMove);

        // ===== ANIMATION =====
        const animate = () => {
            requestAnimationFrame(animate);
            controls.update();
            outerBox.rotation.y += 0.002;
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            mount.removeEventListener("mousemove", handleMouseMove);
            mount.removeChild(renderer.domElement);
            renderer.dispose();
        };
    }, []);

    // ===== HTML LAYER (tooltip) =====
    return (
        <>
            <div
                ref={mountRef}
                style={{ width: "100vw", height: "100vh", overflow: "hidden" }}
            />

            <div
                ref={tooltipRef}
                style={{
                    position: "fixed",
                    padding: "8px 12px",
                    background: "rgba(0,0,0,0.8)",
                    color: "white",
                    borderRadius: "6px",
                    display: "none",
                    pointerEvents: "none",
                    fontSize: "13px",
                    zIndex: 10
                }}
            ></div>
        </>
    );
}
